import { z } from 'zod';
import { dateRangeQuery, pageFields, pageQuery } from './common.js';

/**
 * Merchant-facing audit trail (BR-163, remediation R1 / FND-101): the company's own
 * append-only events, readable by holders of `audit.read`. Platform-only events never appear.
 */
const AUDIT_ACTOR_TYPES = ['user', 'operator', 'system', 'anonymous'] as const;

const auditEntrySchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actorType: z.enum(AUDIT_ACTOR_TYPES),
  actorId: z.string().nullable(),
  /** Full name of the employee; null for system, anonymous and platform actors. */
  actorName: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  ip: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v || undefined);
export const auditListQuerySchema = z.object({
  /** Local days of the merchant (inclusive); real calendar days only (2026-13-45 is refused). */
  ...dateRangeQuery,
  /** Substring of the action key (e.g. "refund", "shift.force_closed"). */
  action: optionalText(60),
  targetType: optionalText(40),
  actorId: z.uuid().optional(),
  ...pageQuery(50),
});
export type AuditListQuery = z.input<typeof auditListQuerySchema>;

export const auditListSchema = z.object({
  entries: z.array(auditEntrySchema),
  ...pageFields,
  /** Distinct action keys of the company, for the filter. */
  actions: z.array(z.string()),
});
export type AuditList = z.infer<typeof auditListSchema>;
